<template>
  <div class="app-product-aid-results">
    <!-- Results View -->
    <template v-if="view === 'results'">
      <header class="app-product-aid-results__header">
        <h2 class="app-product-aid-results__title h3">{{ t('marketingPages.productAid.results.title') }}</h2>
        <p v-if="winner?.product.intro" class="app-product-aid-results__subtitle">
          {{ winner?.product.intro }}
        </p>
      </header>

      <!-- Results List -->
      <div class="app-product-aid-results__list">
        <AppProductAidCard
          v-if="winner"
          variant="winner"
          :title="winner.product.title"
          :percentage="winner.percentage"
          :image="winner.product.image"
          :link="winner.product.link"
          :is-regional="winner.product.isRegional"
          @select-regional="$emit('select-regional')"
        />

        <AppProductAidCard
          v-for="result in secondaryResults"
          :key="result.product.code"
          variant="secondary"
          :title="result.product.title"
          :subtitle="result.product.intro"
          :percentage="result.percentage"
          :image="result.product.image"
          :link="result.product.link"
          :is-regional="result.product.isRegional"
          :expanded="expandedId === result.product.code"
          @toggle="toggleExpand(result.product.code)"
          @select-regional="$emit('select-regional')"
        />
      </div>
    </template>

    <!-- Regional Selection View -->
    <template v-else-if="view === 'regional'">
      <header class="app-product-aid-results__header">
        <h2 class="app-product-aid-results__title">{{ t('marketingPages.productAid.results.regionalTitle') }}</h2>
      </header>

      <div class="app-product-aid-results__list">
        <AppProductAidCard
          v-for="product in regionalProducts"
          :key="product.code"
          variant="regional"
          :title="product.title"
          :subtitle="product.intro"
          :image="product.image"
          :link="product.link"
          :expanded="expandedId === product.code"
          @toggle="toggleExpand(product.code)"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  export type ProductAidProduct = {
    code: string;
    image: string;
    title: string;
    intro?: string;
    link: string;
    isRegional: boolean;
  };

  export type ProductResult = {
    product: ProductAidProduct;
    percentage: number;
    likes: number;
  };

  const { t } = useI18n();

  const props = defineProps<{
    view: 'results' | 'regional';
    winner: ProductResult | null;
    secondaryResults: ProductResult[];
    regionalProducts: ProductAidProduct[];
    expandedId: string | null;
  }>();

  const emit = defineEmits<{
    'select-regional': [];
    'update:expandedId': [id: string | null];
  }>();

  function toggleExpand(id: string) {
    emit('update:expandedId', props.expandedId === id ? null : id);
  }
</script>

<style lang="postcss" scoped>
  .app-product-aid-results {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: var(--spacing-6);
    justify-content: center;

    width: 100%;
    margin: auto;

    &__header {
      color: var(--colors-black);
      text-align: center;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);
    }
  }
</style>
